import { MoneySchema } from "@redotech/money";
import { privacy } from "@redotech/schema-privacy";
import * as z from "zod";

export enum RedoInternalProduct {
  RETURNS = "RETURNS",
  PACKAGE_PROTECTION = "PACKAGE_PROTECTION",
  FINAL_SALE_RETURNS = "FINAL_SALE_RETURNS",
  WARRANTY = "WARRANTY",
  OMS = "OMS",
  WMS = "WMS",
  EMAIL_SMS = "EMAIL_SMS",
  ORDER_TRACKING = "ORDER_TRACKING",
  REVIEW = "REVIEW",
  LOYALTY = "LOYALTY",
  CHECKOUT_OPTIMIZATION = "CHECKOUT_OPTIMIZATION",
  POST_PURCHASE_UPSELL = "POST_PURCHASE_UPSELL",
  CUSTOMER_ACCOUNTS = "CUSTOMER_ACCOUNTS",
  SUPPORT_AI = "SUPPORT_AI",
  IMS = "IMS",
  CATALOG = "CATALOG",
  AEO = "AEO",
  RMR = "RMR",
  LCG = "LCG",
  RECOVER = "RECOVER",
  RECLAIM = "RECLAIM",
  HSA = "HSA",
  XBORDER_RETURNS = "XBORDER_RETURNS",
  ORDER_EDITING = "ORDER_EDITING",
}

export const REDO_INTERNAL_PRODUCT_LABELS: Record<RedoInternalProduct, string> =
  {
    [RedoInternalProduct.RETURNS]: "Returns",
    [RedoInternalProduct.PACKAGE_PROTECTION]: "Package protection",
    [RedoInternalProduct.FINAL_SALE_RETURNS]: "Final sale returns",
    [RedoInternalProduct.WARRANTY]: "Warranty",
    [RedoInternalProduct.OMS]: "OMS",
    [RedoInternalProduct.WMS]: "WMS",
    [RedoInternalProduct.EMAIL_SMS]: "Email/SMS",
    [RedoInternalProduct.ORDER_TRACKING]: "Order tracking",
    [RedoInternalProduct.REVIEW]: "Reviews",
    [RedoInternalProduct.LOYALTY]: "Loyalty",
    [RedoInternalProduct.CHECKOUT_OPTIMIZATION]: "Checkout optimization",
    [RedoInternalProduct.POST_PURCHASE_UPSELL]: "Post purchase upsell",
    [RedoInternalProduct.CUSTOMER_ACCOUNTS]: "Customer accounts",
    [RedoInternalProduct.SUPPORT_AI]: "Support AI",
    [RedoInternalProduct.IMS]: "IMS",
    [RedoInternalProduct.CATALOG]: "Catalog",
    [RedoInternalProduct.AEO]: "AEO",
    [RedoInternalProduct.RMR]: "RMR",
    [RedoInternalProduct.LCG]: "LCG",
    [RedoInternalProduct.RECOVER]: "Recover",
    [RedoInternalProduct.RECLAIM]: "Reclaim",
    [RedoInternalProduct.HSA]: "HSA",
    [RedoInternalProduct.XBORDER_RETURNS]: "Cross-border returns",
    [RedoInternalProduct.ORDER_EDITING]: "Order editing",
  };

const baseInternalProductSchema = z
  .object({
    /**
     * Whether the product is enabled for the team (regardless of whether it's
     * being used).
     */
    isEnabled: z.boolean().meta({ privacy: privacy.flag }),
    /**
     * Whether the product is actually being used.
     */
    isActive: z.boolean().meta({ privacy: privacy.flag }),
    /**
     * Whether we are billing the team for the product.
     */
    isBillingActive: z.boolean().meta({ privacy: privacy.flag }),
  })
  .meta({ privacy: privacy.structured });

// Derived fields carry a `definitionVersion` (bump on meaning change); the version
// literals + descriptions live in the checkout-optimization-derivations registry.
const checkoutOptimizationSchema = baseInternalProductSchema
  .extend({
    additionalDetails: z
      .object({
        conversionBillingEnabled: z.boolean().meta({ privacy: privacy.flag }),
        conversionBillingAccepted: z.boolean().meta({ privacy: privacy.flag }),
        last7DaysBilledOrderCount: z
          .number()
          .meta({ privacy: privacy.quantity })
          .nullish(),
        last7DaysBilledOrderRevenue: MoneySchema.nullish(),
        segmentation: z
          .object({
            types: z.array(z.string()).meta({ privacy: privacy.enumOrStatus }),
            definitionVersion: z.number().meta({ privacy: privacy.quantity }),
          })
          .meta({ privacy: privacy.structured })
          .nullish(),
        checkoutWidgets: z
          .object({
            activeIds: z
              .array(z.string())
              .meta({ privacy: privacy.id.internal }),
            definitionVersion: z.number().meta({ privacy: privacy.quantity }),
          })
          .meta({ privacy: privacy.structured })
          .nullish(),
        intentRescuePanel: z
          .object({
            isActive: z.boolean().meta({ privacy: privacy.flag }),
            definitionVersion: z.number().meta({ privacy: privacy.quantity }),
          })
          .meta({ privacy: privacy.structured })
          .nullish(),
        dimensionalPackaging: z
          .object({
            isActive: z.boolean().meta({ privacy: privacy.flag }),
            definitionVersion: z.number().meta({ privacy: privacy.quantity }),
          })
          .meta({ privacy: privacy.structured })
          .nullish(),
        failedDiscountRescue: z
          .object({
            impactedRevenueTrailing30Day: z
              .number()
              .meta({ privacy: privacy.money }),
            recoveredOrdersTrailing30Day: z
              .number()
              .meta({ privacy: privacy.quantity }),
            avgRecoveredOrderValueTrailing30Day: z
              .number()
              .meta({ privacy: privacy.money }),
            failedDiscountsDetectedTrailing30Day: z
              .number()
              .meta({ privacy: privacy.quantity }),
            rescuesClaimedTrailing30Day: z
              .number()
              .meta({ privacy: privacy.quantity }),
            claimRateTrailing30Day: z
              .number()
              .meta({ privacy: privacy.quantity }),
            definitionVersion: z.number().meta({ privacy: privacy.quantity }),
          })
          .meta({ privacy: privacy.structured })
          .nullish(),
      })
      .meta({ privacy: privacy.structured })
      .nullish(),
  })
  .meta({ privacy: privacy.structured });

const redoInternalProductsSchema_ = z.object({
  status: z
    .object({
      [RedoInternalProduct.RETURNS]: baseInternalProductSchema,
      [RedoInternalProduct.PACKAGE_PROTECTION]: baseInternalProductSchema,
      [RedoInternalProduct.FINAL_SALE_RETURNS]: baseInternalProductSchema,
      [RedoInternalProduct.WARRANTY]: baseInternalProductSchema,
      [RedoInternalProduct.OMS]: baseInternalProductSchema,
      [RedoInternalProduct.WMS]: baseInternalProductSchema,
      [RedoInternalProduct.EMAIL_SMS]: baseInternalProductSchema,
      [RedoInternalProduct.ORDER_TRACKING]: baseInternalProductSchema,
      [RedoInternalProduct.REVIEW]: baseInternalProductSchema,
      [RedoInternalProduct.LOYALTY]: baseInternalProductSchema,
      [RedoInternalProduct.CHECKOUT_OPTIMIZATION]: checkoutOptimizationSchema,
      [RedoInternalProduct.POST_PURCHASE_UPSELL]: baseInternalProductSchema,
      [RedoInternalProduct.CUSTOMER_ACCOUNTS]: baseInternalProductSchema,
      [RedoInternalProduct.SUPPORT_AI]: baseInternalProductSchema,
      [RedoInternalProduct.IMS]: baseInternalProductSchema,
      [RedoInternalProduct.CATALOG]: baseInternalProductSchema,
      [RedoInternalProduct.AEO]: baseInternalProductSchema,
      [RedoInternalProduct.RMR]: baseInternalProductSchema,
      [RedoInternalProduct.LCG]: baseInternalProductSchema,
      [RedoInternalProduct.RECOVER]: baseInternalProductSchema,
      [RedoInternalProduct.RECLAIM]: baseInternalProductSchema,
      [RedoInternalProduct.HSA]: baseInternalProductSchema,
      [RedoInternalProduct.XBORDER_RETURNS]: baseInternalProductSchema,
      [RedoInternalProduct.ORDER_EDITING]: baseInternalProductSchema,
    })
    .meta({ privacy: privacy.structured }),
  lastSyncedAt: z.coerce.date().meta({ privacy: privacy.timestamp }),
});

export const redoInternalProductsSchema = redoInternalProductsSchema_;
export interface RedoInternalProducts extends z.infer<
  typeof redoInternalProductsSchema_
> {}

export function isRedoInternalProductEnabled(
  redoInternalProducts: RedoInternalProducts | undefined,
  product: RedoInternalProduct,
): boolean {
  return !!redoInternalProducts?.status?.[product]?.isEnabled;
}

export function isRedoInternalProductActive(
  redoInternalProducts: RedoInternalProducts | undefined,
  product: RedoInternalProduct,
): boolean {
  return !!redoInternalProducts?.status?.[product]?.isActive;
}

export function getActiveRedoProductLabels(
  redoInternalProducts: RedoInternalProducts | undefined,
): string[] {
  return Object.values(RedoInternalProduct)
    .filter(
      (product) =>
        product !== RedoInternalProduct.CUSTOMER_ACCOUNTS &&
        isRedoInternalProductActive(redoInternalProducts, product),
    )
    .map((product) => REDO_INTERNAL_PRODUCT_LABELS[product])
    .toSorted((a, b) => a.localeCompare(b));
}

export function enabledRedoInternalProducts(
  redoInternalProducts: RedoInternalProducts | undefined,
): Set<RedoInternalProduct> {
  const enabled = new Set<RedoInternalProduct>();
  for (const product of Object.values(RedoInternalProduct)) {
    if (isRedoInternalProductEnabled(redoInternalProducts, product)) {
      enabled.add(product);
    }
  }
  return enabled;
}
